import React, { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import Dialog from './Dialog';
import { useSignedIn } from '../session';
import { AddressSuggestion, GeocodeMatchLevel, PlaceSuggestion } from '../types';
import AddressMap from './AddressMap';

/** Ono što je komponenti potrebno o adresi, bez obzira odakle je došla. */
export interface FixableAddress {
  id: number;
  erpDocumentKey: string;
  rawAddress: string;
  rawCity: string;
  resolvedCity: string | null;
  postalCode: string | null;
  latitude: number | null;
  longitude: number | null;
  matchLevel: GeocodeMatchLevel;
  note: string | null;
  placeSuggestions: PlaceSuggestion[];
}

interface Props {
  address: FixableAddress;
  /** Ko potvrđuje. Do prijavljivanja je to slobodan tekst. */
  /** Zove se posle uspešne potvrde ili odbijanja. */
  onDone: (addressId: number) => void;
}

interface Pin {
  latitude: number;
  longitude: number;
}

/**
 * Ispravka jedne adrese: mapa, predlog savetnika, pristup, potvrda.
 *
 * Isti posao rade operater u magacinu i poslovođa u prodavnici, samo iz
 * različitih razloga — operater ih radi desetine nedeljno u nizu, poslovođa
 * jednu po jednu dok mu je kupac na vezi. Zato je ovo jedna komponenta: da se
 * pravilo o tome šta sme u rutu ne raziđe na dva ekrana.
 *
 * Sprat i lift se traže baš ovde jer se odgovor dobija od kupca, a ne sa mape.
 */
export default function AddressFixer({
  address, onDone
}: Props) {
  const signedIn = useSignedIn();
  const [pin, setPin] = useState<Pin | null>(null);
  const [floor, setFloor] = useState('');
  const [hasLift, setHasLift] = useState(false);
  const [twoPeople, setTwoPeople] = useState(false);
  const [accessNotes, setAccessNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [suggestions, setSuggestions] = useState<AddressSuggestion[] | null>(null);
  const [unverified, setUnverified] = useState<string[]>([]);
  const [asking, setAsking] = useState(false);

  /** Odakle je pin, kad nije iz onoga što piše na otpremnici. */
  const [pinFrom, setPinFrom] = useState<string | null>(null);

  /** Za koju adresu je savetnik već pitan, da se isti poziv ne plaća dvaput. */
  const askedFor = useRef<number | null>(null);

  // Pri prelasku na drugu adresu pin skače na predlog geokodera, a podaci o
  // pristupu se brišu — inače bi sprat prethodne adrese tiho ostao na sledećoj.
  useEffect(() => {
    setPin(
      address.latitude !== null && address.longitude !== null
        ? { latitude: address.latitude, longitude: address.longitude }
        : null
    );
    setFloor('');
    setHasLift(false);
    setTwoPeople(false);
    setAccessNotes('');
    setSuggestions(null);
    setUnverified([]);
    setPinFrom(null);
    setError(null);
  }, [address]);

  /**
   * Pita savetnika kako bi se ulica mogla zvati.
   *
   * Prazan spisak je uredan ishod — savetnik ćuti kad nema razložan predlog,
   * jer je nagađanje gore od ćutanja. Kvar se prijavljuje odvojeno, da se ta
   * dva ne pomešaju.
   */
  const askAdviser = async () => {
    setAsking(true);
    setError(null);
    try {
      const result = await api.suggestAddress(address.id);
      setSuggestions(result.suggestions);
      setUnverified(result.unverified);

      // Adresa bez tačke dobija pin odmah, iz najboljeg predloga. Pomera se i
      // dalje rukom i ništa nije potvrđeno dok se ne pritisne dugme — ali se ne
      // gleda prazna mapa kad rešenje već postoji.
      if (result.suggestions.length > 0 && address.latitude === null) {
        const best = result.suggestions[0];
        setPin({ latitude: best.latitude, longitude: best.longitude });
        setPinFrom(best.address);
      }

      if (!result.available) {
        setError('Savetnik za adrese nije podešen — nedostaje ključ.');
      } else if (result.error) {
        setError(result.error);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAsking(false);
    }
  };

  // Savetnik se pita sam za adresu koju geokoder nije umeo da postavi, i to tek
  // kad je neko otvori. Ranije je to bilo samo dugme, pa je predlog stajao
  // neviđen: vidi se prazna mapa i „nije nađena", i nema razloga pretpostaviti
  // da iza dugmeta nešto ima. Jednom po adresi — poziv se plaća.
  useEffect(() => {
    if (address.latitude !== null || askedFor.current === address.id) {
      return;
    }

    askedFor.current = address.id;
    void askAdviser();
    // Zavisi se samo od otvorene adrese; askAdviser se menja pri svakom
    // iscrtavanju i uveo bi poziv u krug.
  }, [address]);

  const confirm = async () => {
    if (!pin) return;

    const name = signedIn.displayName;
    if (!name) {
      setError('Upiši ime pre potvrde — potvrda mora imati potpis.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.confirmAddress(address.id, {
        latitude: pin.latitude,
        longitude: pin.longitude,
        confirmedBy: name,
        resolvedCity: address.resolvedCity,
        postalCode: address.postalCode,
        floor: floor.trim() === '' ? null : Number(floor),
        hasLift,
        accessNotes: accessNotes.trim() || null,
        requiresTwoPeople: twoPeople
      });
      onDone(address.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Razlog odbijanja. Traži se u dijalogu, ne u prozorčiću pregledača. */
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const reject = async () => {
    const reason = rejectReason.trim();
    if (reason === '') return;

    setBusy(true);
    setError(null);
    try {
      await api.rejectAddress(address.id, reason, signedIn.displayName);
      setRejecting(false);
      setRejectReason('');
      onDone(address.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && <div className="health health--bad">{error}</div>}

      <p className={pin ? 'explanation' : 'explanation explanation--warn'}>{address.note}</p>

      {address.placeSuggestions.length > 0 && (
        <p className="note">
          Mesto nije u šifarniku. Najbliže:{' '}
          {address.placeSuggestions.map((s) => `${s.name} (${s.postalCode})`).join(', ')}.
          Ispravi ga u ERP-u, pa pusti geokoder ponovo.
        </p>
      )}

      <AddressMap
        latitude={pin?.latitude ?? null}
        longitude={pin?.longitude ?? null}
        tight={address.matchLevel === 'HouseNumber'}
        onPick={(latitude, longitude) => setPin({ latitude, longitude })}
      />

      <p className="muted map__hint">
        {pin
          ? `Pin: ${pin.latitude.toFixed(5)}, ${pin.longitude.toFixed(5)} — klikni na mapu ili prevuci da ispraviš.`
          : 'Geokoder nije ponudio tačku. Klikni na mapu da postaviš pin.'}
      </p>

      {/* Odakle je pin, kad nije iz onoga što piše na otpremnici. Bez ove
          rečenice bi se tačka potvrdila ne znajući da je dobijena za drugačije
          napisanu ulicu. */}
      {pinFrom && (
        <p className="explanation">
          Pin je postavljen po predlogu <strong>{pinFrom}</strong>, ne po tekstu sa
          otpremnice. Proveri da je to ta kuća pre potvrde.
        </p>
      )}

      {/* Predlog ne menja adresu u ERP-u — samo pomera pin i kaže šta je
          verovatno pisalo. Ispravku teksta radi prodavnica, tamo gde je greška
          i nastala. */}
      <div className="suggest">
        <button type="button" onClick={() => void askAdviser()} disabled={asking}>
          {asking ? 'Pitam…' : 'Predloži ulicu'}
        </button>

        {suggestions?.length === 0 && unverified.length === 0 && (
          <span className="muted"> Savetnik nema predlog za ovu adresu.</span>
        )}

        {suggestions && suggestions.length > 0 && (
          <ul className="suggest__list">
            {suggestions.map((s) => (
              <li key={s.address}>
                <button
                  type="button"
                  onClick={() => {
                    setPin({ latitude: s.latitude, longitude: s.longitude });
                    setPinFrom(s.address);
                  }}
                >
                  <strong>{s.address}</strong>
                  <span className="muted"> {s.reason}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Ovo mapa ne poznaje, pa nema tačku i ne može se kliknuti. Stoji jer
            onome ko pin postavlja rukom razlika između „Doze Djerdja" i „Dože
            Đerđa" kaže gde uopšte da gleda. */}
        {unverified.length > 0 && (
          <p className="muted suggest__unverified">
            Savetnik misli da je: {unverified.join(', ')} — ali te ulice nema na
            mapi, pa pin postavi ručno.
          </p>
        )}
      </div>

      <div className="access">
        <label className="field">
          Sprat
          <input
            type="number"
            value={floor}
            onChange={(e) => setFloor(e.target.value)}
            placeholder="prizemlje"
          />
        </label>
        <label className="check">
          <input type="checkbox" checked={hasLift} onChange={(e) => setHasLift(e.target.checked)} />
          Ima lift
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={twoPeople}
            onChange={(e) => setTwoPeople(e.target.checked)}
          />
          Traži dva čoveka
        </label>
      </div>

      <label className="field">
        Pristup
        <input
          type="text"
          value={accessNotes}
          onChange={(e) => setAccessNotes(e.target.value)}
          placeholder="uzak prolaz, kapija iza zgrade…"
        />
      </label>

      <div className="queue__confirm">
        <button type="button" onClick={() => void confirm()} disabled={busy || !pin}>
          Potvrdi adresu
        </button>
        <button
          type="button"
          className="link link--danger"
          onClick={() => setRejecting(true)}
          disabled={busy}
        >
          Odbij
        </button>

        {rejecting && (
          <Dialog
            title="Odbijanje adrese"
            description={
              <>Adresa se sklanja sa spiska i isporuka se ne može planirati dok se
              ne sredi. Razlog ostaje zapisan uz nju.</>
            }
            confirmLabel="Odbij adresu"
            danger
            busy={busy}
            disabled={rejectReason.trim() === ''}
            onCancel={() => { setRejecting(false); setRejectReason(''); }}
            onConfirm={() => void reject()}
          >
            <label>
              Zašto se adresa odbija?
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="npr. kupac je dao adresu koja ne postoji, čeka se ispravka"
              />
            </label>
          </Dialog>
        )}
      </div>
    </>
  );
}
